import { TrackingCategory } from '@tn/foundation/entities/tracking/const';
import { TrackerEngine } from '@tn/foundation/entities/tracking/lib/tracker-engine';
import { LOG_TYPES, PAGE_TYPES, TRACK_LINK_NAMES } from '@tn/foundation/utils/constants/tracking';

import { devLog } from '../../utils/devLog';
import { RENDEZVOUS_API_CLIENT_ERROR_CODES_MAP } from '../tv/graphql/constants';

import type { ActivationScreen, DeviceInfo } from './types';
import { DEFAULT_ACTIVATION_SCREEN, RENDEZVOUS_LOCAL_STORAGE_KEY } from './types';

export type RendezvousAppLogFields = {
    appLogText: string;
    appLogType: string;
    appLogCode: string;
};

type GraphqlSubmitError = {
    response?: {
        errors?: Array<{
            message?: string;
            extensions?: {
                code?: string;
                error?: string;
            };
        }>;
    };
    message?: string;
    appLogType?: string;
    appLogCode?: string;
};

export function getRendezvousAppLogFromSubmitError(error: unknown): RendezvousAppLogFields {
    const gqlError =
        error != null && typeof error === 'object'
            ? (error as GraphqlSubmitError).response?.errors?.[0]
            : undefined;

    if (gqlError) {
        const appLogCode =
            typeof gqlError.extensions?.code === 'string' ? gqlError.extensions.code : '';
        const appLogTypeFromExtension =
            typeof gqlError.extensions?.error === 'string' ? gqlError.extensions.error : '';
        const isSanitizedProductionError = Boolean(appLogCode) && !appLogTypeFromExtension;

        return {
            appLogText: isSanitizedProductionError
                ? (RENDEZVOUS_API_CLIENT_ERROR_CODES_MAP[appLogCode] ??
                  RENDEZVOUS_API_CLIENT_ERROR_CODES_MAP.DEFAULT)
                : (gqlError.message ?? ''),
            appLogType: appLogTypeFromExtension || (appLogCode ? LOG_TYPES.AUTHENTICATION : ''),
            appLogCode,
        };
    }

    if (error instanceof Error) {
        const structuredError = error as Error & { appLogType?: string; appLogCode?: string };
        return {
            appLogText: structuredError.message,
            appLogType: structuredError.appLogType ?? '',
            appLogCode: structuredError.appLogCode ?? '',
        };
    }

    return {
        appLogText: '',
        appLogType: '',
        appLogCode: '',
    };
}

function getPageUrlForTracking(): string {
    if (typeof globalThis === 'undefined' || !globalThis.location?.href) {
        return '';
    }
    return globalThis.location.href;
}

/**
 * Map rendezvous state machine device info to tracking context
 */
export function getTrackingDeviceContext(params: Partial<DeviceInfo>) {
    return {
        ...(typeof params.activationCode === 'string'
            ? { activationShortCode: params.activationCode }
            : {}),
        ...(typeof params.device?.name === 'string'
            ? { ottActivationPartner: params.device.name }
            : {}),
        ...(typeof params.device?.partner === 'string'
            ? { activationDeviceFamily: params.device.partner }
            : {}),
        ...(typeof params.activationScreen === 'string'
            ? { activationScreen: params.activationScreen }
            : {}),
    };
}

/** Full activation eventInfo for enter-code activate click — always includes spec keys (empty when unknown). */
function getActivateButtonClickEventInfo(params: Partial<DeviceInfo>) {
    return {
        ottActivationPartner: typeof params.device?.name === 'string' ? params.device.name : '',
        activationDeviceFamily:
            typeof params.device?.partner === 'string' ? params.device.partner : '',
        activationShortCode: typeof params.activationCode === 'string' ? params.activationCode : '',
        activationScreen:
            typeof params.activationScreen === 'string'
                ? params.activationScreen
                : DEFAULT_ACTIVATION_SCREEN,
    };
}

export const trackActivateButtonClickEvent = (params: Partial<DeviceInfo>) => {
    try {
        void TrackerEngine.update({
            type: 'click',
            detail: {
                context: {
                    linkname: 'trackActivation',
                    pageUrl: getPageUrlForTracking(),
                    ...getActivateButtonClickEventInfo(params),
                },
            },
            category: TrackingCategory.App.INTERACT,
        });
    } catch {
        devLog('[trackActivateButtonClickEvent] error', () => params);
    }
};

export const readStoredDeviceInfo = (): Partial<DeviceInfo> => {
    try {
        const raw = globalThis.localStorage?.getItem(RENDEZVOUS_LOCAL_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const saved: unknown = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') {
            return {};
        }
        const { activationCode, activationScreen, deviceName, devicePartner } = saved as Record<
            string,
            unknown
        >;
        return {
            ...(typeof activationCode === 'string' ? { activationCode } : {}),
            ...(typeof activationScreen === 'string'
                ? { activationScreen: activationScreen as ActivationScreen }
                : {}),
            ...(typeof deviceName === 'string' || typeof devicePartner === 'string'
                ? {
                      device: {
                          name: typeof deviceName === 'string' ? deviceName : '',
                          partner: typeof devicePartner === 'string' ? devicePartner : '',
                      },
                  }
                : {}),
        };
    } catch {
        return {};
    }
};

export const resolveDeviceInfoForTracking = (
    params?: Partial<DeviceInfo> | null,
): Partial<DeviceInfo> => {
    if (params?.activationCode) {
        return params;
    }
    const stored = readStoredDeviceInfo();
    return stored.activationCode ? stored : (params ?? {});
};

/** Device context for activation success PAGE_VIEW — omits activationShortCode per spec. */
export function getActivationSuccessDeviceContext(params?: Partial<DeviceInfo> | null) {
    const { activationShortCode: _activationShortCode, ...deviceContext } =
        getTrackingDeviceContext(resolveDeviceInfoForTracking(params));
    return deviceContext;
}

export const SIGN_IN_OPTIONS_CTA_LINKNAMES = {
    SIGN_IN: 'trackLogin',
    SIGN_UP: 'trackSignUp',
    MVPD: 'trackSignInTvProvider',
} as const;

export type SignInOptionsCtaLinkname =
    (typeof SIGN_IN_OPTIONS_CTA_LINKNAMES)[keyof typeof SIGN_IN_OPTIONS_CTA_LINKNAMES];

export const trackSignInOptionsCtaClickEvent = (
    params: Partial<DeviceInfo>,
    cta: { linkname: SignInOptionsCtaLinkname; ctaText?: string },
) => {
    try {
        void TrackerEngine.update({
            type: 'click',
            detail: {
                context: {
                    linkname: cta.linkname,
                    ...(cta.ctaText ? { ctaText: cta.ctaText } : {}),
                    pageType: PAGE_TYPES.RENDEZVOUS_ACTIVATION_SIGNIN,
                    pageUrl: getPageUrlForTracking(),
                    ...getTrackingDeviceContext(resolveDeviceInfoForTracking(params)),
                },
            },
            category: TrackingCategory.App.INTERACT,
        });
    } catch {
        devLog('[trackSignInOptionsCtaClickEvent] error', () => params);
    }
};

export type TrackActivationFailureParams = Partial<DeviceInfo> &
    RendezvousAppLogFields & {
        pageType: string;
    };

export const trackActivationFailureEvent = (params: TrackActivationFailureParams) => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_APP_LOG,
            category: TrackingCategory.App.LOG,
            detail: {
                context: {
                    linkName: TRACK_LINK_NAMES.TRACK_APP_LOG,
                    pageUrl: getPageUrlForTracking(),
                    pageType: params.pageType,
                    appLogText: params.appLogText,
                    appLogType: params.appLogType,
                    appLogCode: params.appLogCode,
                    ...getTrackingDeviceContext(params),
                },
            },
        });
    } catch {
        devLog('[trackActivationFailureEvent] error', () => params);
    }
};

export default trackActivateButtonClickEvent;
